import { DefineComponent, FunctionalComponent, ref } from "vue";

const classNames =
{
    container :"labeledToggleSwitch__container",
    input: "labeledToggleSwitch__input",
    toggle: "labeledToggleSwitch__toggle",
    name: "labeledToggleSwitch__name",
    selectedName: "labeledToggleSwitch__name--selected",
};

type LabeledToggleSwitchProps =
{
    CheckedStateLabel: string;
    UncheckedStateLabel: string;
    checked?: boolean;
    id?: string;
    name: string;
    disabled?: boolean;
    ariaLabel?: string;
    onChange?: (e: Event) => void;
    containerClassOverride?: string,
    itemClassOverride?: string,
    toggleClassOverride?: string,
};

type LabeledToggleSwitchContext =
{
    "update:checked": (checked: boolean) => void;
};

const LabeledToggleSwitch: FunctionalComponent<LabeledToggleSwitchProps, LabeledToggleSwitchContext> = function vButton(props: LabeledToggleSwitchProps, context)
{
    const isChecked = ref<boolean>(props.checked ?? false);
    const inputElement = ref<HTMLInputElement>();
    const liveRegion = ref<HTMLSpanElement>();

    function changeValue(manual: boolean)
    {
        if (props.disabled)
        {
            return;
        }
        if (manual)
        {
            inputElement.value!.checked = !inputElement.value!.checked;
        }
        isChecked.value = inputElement.value!.checked;
        context.emit("update:checked", isChecked.value);

        if (liveRegion.value)
        {
            liveRegion.value.innerHTML = isChecked.value ? props.CheckedStateLabel : props.UncheckedStateLabel;
        }
    }

    function handleFocus()
    {
        if (liveRegion.value)
        {
            liveRegion.value.innerHTML += "&nbsp";
        }
    };

    return (
        <label role="checkbox"
            id={props.id ? `${props.id}_labeledToggleSwitch` : undefined}
            tabindex="0"
            aria-label={props.ariaLabel}
            aria-checked={isChecked.value}
            onFocus={handleFocus}
            class=
            {{
                [classNames.container]: props.containerClassOverride == undefined,
                [props.containerClassOverride!]: props.containerClassOverride != undefined,
                "ui-control": true,
                "ui-disabled": props.disabled === true
            }}
            {...context.attrs}
            onKeydown={(e: KeyboardEvent) => { if (e.key === " ") { changeValue(true); }}}>
            <input ref={inputElement}
                id={props.id}
                class={{ [classNames.input]: true }}
                name={props.name}
                type="checkbox"
                aria-hidden={true}
                tabindex="-1"
                checked={isChecked.value}
                onChange={() => changeValue(false)} />
            <div class=
            {{ 
                [classNames.toggle]: props.toggleClassOverride == undefined,
                [props.toggleClassOverride!]: props.toggleClassOverride != undefined,
            }}></div>
            <div class=
            {{
                [classNames.name]: props.itemClassOverride == undefined,
                [props.itemClassOverride!]: props.itemClassOverride != undefined,
                [classNames.selectedName]: !isChecked.value
            }}>{props.UncheckedStateLabel}</div>
            <div class=
            {{
                [classNames.name]: props.itemClassOverride == undefined,
                [props.itemClassOverride!]: props.itemClassOverride != undefined,
                [classNames.selectedName]: isChecked.value
            }}>{props.CheckedStateLabel}</div>
            <span ref={liveRegion} aria-live="polite" class="sr-only">{isChecked.value ? props.CheckedStateLabel : props.UncheckedStateLabel}</span>
        </label>
    );
};

export default LabeledToggleSwitch as unknown as DefineComponent<LabeledToggleSwitchProps, LabeledToggleSwitchContext, JSX.Element>;
